import {
  ELEVATION_COURSE_DOWNHILL_END_X,
  ELEVATION_COURSE_ROAD_HALF_WIDTH,
  ELEVATION_COURSE_UPHILL_START_X,
} from './drivingTerrain';
import { isBraking, type VehicleInput, type VehicleState } from './vehicleDriving';
import type { DrivingChallengeId, DrivingChallengeOption } from '../data/challengeOptions';

/**
 * 주행 챌린지.
 *
 * 일반 랩 타이머(drivingSession)와 분리합니다. 챌린지는 "무장 → 시작 → 성공/무효"라는
 * 자체 상태 기계를 갖고, 시작 조건과 종료 조건이 종목마다 다릅니다. 세션에 섞으면
 * 자유 주행에서도 그 규칙이 돌아가게 됩니다.
 *
 * 시간은 받은 고정 스텝 dt를 더해서 재고 performance.now를 쓰지 않습니다. 그래야 탭을
 * 벗어났다 돌아와도 기록이 오염되지 않고 테스트가 결정론적입니다.
 */

export type ChallengeStatus = 'idle' | 'armed' | 'running' | 'complete' | 'invalid';

export interface DrivingChallengeState {
  challengeId: DrivingChallengeId;
  status: ChallengeStatus;
  /** 진행 중 경과 시간(초). */
  elapsedSeconds: number;
  /** 진행 중 이동 거리(m). 제동 종목에서 제동 거리입니다. */
  distanceMetres: number;
  /** 종목의 현재 측정값. 0→100·힐클라임은 초, 제동은 m, 최고속도는 km/h입니다. */
  resultValue: number | null;
  bestValue: number | null;
  /** HUD에 그대로 띄우는 짧은 안내 문구입니다. */
  message: string;
  observedSpawnCount: number;
}

const HUNDRED_KMH = 100 / 3.6;
/** 최고속도 종목의 측정 시간(초). */
const TOP_SPEED_WINDOW = 30;

export function createDrivingChallengeState(
  challengeId: DrivingChallengeId,
  spawnCount = 0,
  bestValue: number | null = null
): DrivingChallengeState {
  return {
    challengeId,
    status: challengeId === 'free-drive' ? 'idle' : 'armed',
    elapsedSeconds: 0,
    distanceMetres: 0,
    resultValue: null,
    bestValue,
    message: initialMessage(challengeId),
    observedSpawnCount: spawnCount,
  };
}

function initialMessage(challengeId: DrivingChallengeId): string {
  switch (challengeId) {
    case 'zero-to-100':
      return '정지 상태에서 가속하세요';
    case 'top-speed':
      return '출발하면 30초간 측정합니다';
    case 'braking-100':
      return '100km/h에서 제동하세요';
    case 'hill-climb':
      return '출발하면 결승선까지 측정합니다';
    default:
      return '자유 주행';
  }
}

/** 두 스텝 사이에서 임계 속도를 지난 시점을 선형 보간합니다. */
function crossingFraction(previous: number, next: number, threshold: number): number {
  const span = next - previous;
  if (Math.abs(span) < 1e-9) {
    return 1;
  }
  return Math.min(1, Math.max(0, (threshold - previous) / span));
}

export function isChallengeLowerBetter(challengeId: DrivingChallengeId): boolean {
  return challengeId !== 'top-speed';
}

/** 물리 직후 한 프레임 진행. CarDriver가 세션 갱신과 같은 콜백에서 호출합니다. */
export function stepDrivingChallenge(
  state: DrivingChallengeState,
  previous: VehicleState,
  next: VehicleState,
  input: VehicleInput,
  dt: number,
  challenge: DrivingChallengeOption,
  courseId?: string
): DrivingChallengeState {
  if (state.challengeId !== challenge.id) {
    return createDrivingChallengeState(challenge.id, next.spawnCount);
  }
  if (challenge.id === 'free-drive') {
    return state;
  }
  /*
   * 코스를 요구하는 종목은 그 코스에서만 측정합니다. 이 검사가 없으면 힐클라임의 결승선
   * (x=560)을 외곽 링이 한 바퀴마다 통과해서 엉뚱한 기록이 베스트로 남습니다.
   */
  if (
    challenge.requiredCourseId !== undefined &&
    courseId !== undefined &&
    courseId !== challenge.requiredCourseId
  ) {
    return state.status === 'idle'
      ? state
      : { ...state, status: 'idle', message: '이 챌린지는 다른 코스에서 측정합니다' };
  }
  // 리스폰·코스 변경은 진행 중인 기록을 무효로 하고 다시 무장합니다.
  if (next.spawnCount !== state.observedSpawnCount) {
    return createDrivingChallengeState(challenge.id, next.spawnCount, state.bestValue);
  }
  if (state.status === 'complete' || state.status === 'invalid') {
    return state;
  }

  const acceptedDt = Math.min(Math.max(dt, 0), 1 / 20);
  const speed = Math.abs(next.speed);
  const pathDistance = Math.hypot(next.x - previous.x, next.z - previous.z);
  const working = { ...state };

  if (challenge.id === 'zero-to-100') {
    if (working.status === 'armed') {
      if (next.speed < -0.5) {
        return { ...working, status: 'invalid', message: '후진으로 무효 · 출발선으로 재시작' };
      }
      if (previous.speed <= 0.05 && next.speed > 0.05) {
        working.status = 'running';
        working.elapsedSeconds = 0;
        working.message = '측정 중';
      }
      return working;
    }
    working.elapsedSeconds += acceptedDt;
    working.distanceMetres += pathDistance;
    if (Math.abs(previous.speed) < HUNDRED_KMH && speed >= HUNDRED_KMH) {
      // 프레임 사이에 임계를 지났으므로 지난 시점까지만 계산합니다.
      const fraction = crossingFraction(Math.abs(previous.speed), speed, HUNDRED_KMH);
      const result = working.elapsedSeconds - acceptedDt * (1 - fraction);
      return finish(working, result, `0→100 ${result.toFixed(2)}초`);
    }
    if (working.elapsedSeconds > 60) {
      return { ...working, status: 'invalid', message: '시간 초과 · 출발선으로 재시작' };
    }
    return working;
  }

  if (challenge.id === 'top-speed') {
    if (working.status === 'armed') {
      if (speed > 1) {
        working.status = 'running';
        working.elapsedSeconds = 0;
        working.resultValue = 0;
        working.message = '측정 중';
      }
      return working;
    }
    working.elapsedSeconds += acceptedDt;
    working.distanceMetres += pathDistance;
    const peak = Math.max(working.resultValue ?? 0, speed * 3.6);
    working.resultValue = peak;
    working.message = `최고 ${Math.round(peak)} km/h · ${Math.max(
      0,
      TOP_SPEED_WINDOW - working.elapsedSeconds
    ).toFixed(0)}초 남음`;
    if (working.elapsedSeconds >= TOP_SPEED_WINDOW) {
      return finish(working, peak, `최고속도 ${Math.round(peak)} km/h`);
    }
    return working;
  }

  if (challenge.id === 'braking-100') {
    if (working.status === 'armed') {
      working.message =
        speed * 3.6 >= 95
          ? '제동하세요'
          : `가속: ${Math.round(speed * 3.6)} / 100 km/h`;
      if (speed * 3.6 >= 95 && isBraking(input)) {
        working.status = 'running';
        working.elapsedSeconds = 0;
        working.distanceMetres = 0;
        working.resultValue = 0;
        working.message = '제동 중';
      }
      return working;
    }
    working.elapsedSeconds += acceptedDt;
    working.distanceMetres += pathDistance;
    working.resultValue = working.distanceMetres;
    if (!isBraking(input) && speed > 0.5) {
      return { ...working, status: 'invalid', message: '제동 해제로 무효 · 출발선으로 재시작' };
    }
    if (speed <= 0.5) {
      return finish(
        working,
        working.distanceMetres,
        `100→0 ${working.distanceMetres.toFixed(1)}m · ${working.elapsedSeconds.toFixed(1)}초`
      );
    }
    return working;
  }

  // 힐클라임: 고저차 코스의 결승선 X를 지나면 종료합니다.
  if (working.status === 'armed') {
    if (speed > 1) {
      working.status = 'running';
      working.elapsedSeconds = 0;
      working.message = '측정 중';
    }
    return working;
  }
  working.elapsedSeconds += acceptedDt;
  working.distanceMetres += pathDistance;
  /*
   * 결승선은 좌표 하나가 아니라 "고저차 코스 차선 안에서 +X로 통과"입니다. 좌표만 보면
   * 다른 코스나 역주행에서도 완주로 잡힙니다.
   */
  const onHillLane =
    Math.abs(next.z) <= ELEVATION_COURSE_ROAD_HALF_WIDTH + 2 &&
    next.x >= ELEVATION_COURSE_UPHILL_START_X - 40;
  if (
    onHillLane &&
    previous.x < ELEVATION_COURSE_DOWNHILL_END_X &&
    next.x >= ELEVATION_COURSE_DOWNHILL_END_X
  ) {
    const fraction = crossingFraction(previous.x, next.x, ELEVATION_COURSE_DOWNHILL_END_X);
    const result = working.elapsedSeconds - acceptedDt * (1 - fraction);
    return finish(working, result, `힐클라임 ${result.toFixed(2)}초`);
  }
  if (working.elapsedSeconds > 180) {
    return { ...working, status: 'invalid', message: '시간 초과 · 출발선으로 재시작' };
  }
  return working;
}

function finish(
  state: DrivingChallengeState,
  result: number,
  message: string
): DrivingChallengeState {
  const lowerBetter = isChallengeLowerBetter(state.challengeId);
  const best =
    state.bestValue === null
      ? result
      : lowerBetter
        ? Math.min(state.bestValue, result)
        : Math.max(state.bestValue, result);
  return {
    ...state,
    status: 'complete',
    resultValue: result,
    bestValue: best,
    message: `${message} · 완료`,
  };
}

export function formatChallengeValue(
  challengeId: DrivingChallengeId,
  value: number | null
): string {
  if (value === null) {
    return '--';
  }
  if (challengeId === 'top-speed') {
    return `${Math.round(value)} km/h`;
  }
  if (challengeId === 'braking-100') {
    return `${value.toFixed(1)} m`;
  }
  return `${value.toFixed(2)} 초`;
}
